import React from 'react';
import { createRoot } from 'react-dom/client';
import LRSnapshot from './Pages/LRSnapshot/LRSnapshot';

// LRsnapshot — standalone mount for the Layered Read snapshot widget.
//
// Embed one or more strips on any page:
//   <div data-lrsnapshot
//        data-hash="<movie hash>"
//        data-config='{"grains":["scene"],"metric":"pressure"}'></div>
//   <script type="module" src="{{ mix('js/lrSnapshotApp.js') }}"></script>
//
// The widget fetches ALL of the script's DER once (by hash) and then the caller can
// change what is displayed WITHOUT a refetch:
//   window.LRSnapshot.setConfig('<el id>', { metric: 'freshness', grains: ['sequence','scene'] });

const registry = new Map();

function _injectStyles() {
  if (document.getElementById('lrs-styles')) return;
  const css = `
  .lrs-root{font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;color:#1f2933}
  .lrs-loading,.lrs-error{padding:14px;color:#7a7770;font-size:14px}
  .lrs-strip{display:flex;flex-direction:column;gap:6px}
  .lrs-row{display:flex;width:100%;height:34px;border-radius:6px;overflow:hidden}
  .lrs-cell{position:relative;height:100%;min-width:3px;padding:0;margin:0;cursor:pointer;
            display:flex;align-items:center;justify-content:center;overflow:hidden;
            font-size:10px;font-weight:700;color:rgba(0,0,0,.55);transition:filter .12s}
  .lrs-cell:hover{filter:brightness(1.06) saturate(1.05);z-index:1;outline:2px solid #14536e}
  .lrs-cell:focus-visible{outline:2px solid #14536e;z-index:2}
  .lrs-cell-label{pointer-events:none;white-space:nowrap;text-overflow:ellipsis;overflow:hidden;padding:0 2px}
  .lrs-modal-overlay{position:fixed;inset:0;background:rgba(15,25,35,.55);display:flex;
            align-items:center;justify-content:center;z-index:9999;padding:24px}
  .lrs-modal{position:relative;background:#fff;border-radius:12px;max-width:min(920px,94vw);
            width:100%;max-height:90vh;overflow:auto;box-shadow:0 24px 60px rgba(0,0,0,.35)}
  .lrs-modal-body{padding:18px}
  /* Patterns (2026-10-23): the app's Patterns tab in the snapshot. Button sits at the end of the
     controls row; a pattern's units are outlined on the strip and the rest of that row dims. */
  .lrs-modal--patterns{max-width:min(1040px,94vw)}
  .lrs-pat-title{margin:4px 40px 4px 12px;font:700 1.35rem/1.25 system-ui,sans-serif;color:#0f172a}
  .lrs-pat-lede{margin:0 12px 4px;color:#475569;font:.95rem/1.45 system-ui,sans-serif}
  /* Navy, not the app's rose: on a landing page red reads as an error (owner, 2026-10-23), and a
     count in a red circle looked like a close button. The count is plain text in the label. */
  .lrs-pat-btn{margin-left:auto;display:inline-flex;align-items:center;height:32px;width:auto;padding:0 14px;border:1px solid #14536e;border-radius:999px;background:#fff;color:#14536e;font:700 .86rem/1 system-ui,sans-serif;cursor:pointer}
  .lrs-pat-btn:hover{background:#eef5f8}
  .lrs-marked{display:flex;flex-wrap:wrap;align-items:center;gap:6px 12px;margin:0 0 8px;padding:8px 12px;border-radius:8px;background:#eef5f8;color:#0f3346;font:.88rem/1.4 system-ui,sans-serif}
  .lrs-marked span{flex:1 1 260px}
  .lrs-marked button{width:auto;height:auto;padding:0;border:0;border-radius:0;background:none;color:#14536e;font:700 .84rem/1.3 system-ui,sans-serif;cursor:pointer}
  .lrs-marked button:hover{text-decoration:underline}
  .lrs-pat-reveal{position:sticky;top:0;z-index:3;display:block;width:100%;height:auto;margin:0;padding:10px 48px 10px 18px;border:0;border-radius:0;background:#14536e;color:#fff;text-align:left;font:700 .9rem/1.35 system-ui,sans-serif;cursor:pointer}
  .lrs-pat-reveal:hover{background:#0f4058}
  .lrs-cell--marked{outline:2px solid #0f172a;outline-offset:-2px;z-index:1}
  .lrs-cell--unmarked{filter:saturate(.25) opacity(.35)}
  .lrs-modal-close{position:absolute;top:8px;right:10px;border:none;background:transparent;
            font-size:26px;line-height:1;color:#7a7770;cursor:pointer;z-index:2}
  /* One row: the "Colour by" select and the colour key sit side by side, as they do on the
     Layered Read page (Icicle.js:1500). flex-wrap so a narrow embed drops the key to its own line
     rather than squeezing the control. The legend brings Bootstrap's own px-3/pb-2/pt-1 padding,
     so its left padding IS the gap on the first row — hence the modest gap here. */
  .lrs-controls{display:flex;align-items:center;flex-wrap:wrap;gap:2px 4px;margin:0 0 10px}
  /* #sos-legend is the shared SunburstLegend node. Its bottom padding is tuned for sitting under a
     wheel; on this row it just pushes the strip down. */
  .lrs-controls #sos-legend{padding-bottom:0!important;padding-top:0!important}
  /* .lrs-metric-head / -eyebrow / -name were removed 2026-08-28: a 10.5px uppercase grey label
     over a 20px name, restating what the select one line below already said. Do not add them back
     (CLAUDE.md UI rule 6 — the eyebrow ban is about the pattern, not only the styling). */
  .lrs-info-wrap{position:relative;display:inline-flex}
  .lrs-info{width:18px;height:18px;border-radius:50%;border:1.5px solid #b9c6cd;background:#fff;color:#5a6b76;font:800 11px/1 system-ui,sans-serif;cursor:pointer;display:inline-flex;align-items:center;justify-content:center}
  .lrs-info:hover{border-color:#14536e;color:#14536e}
  /* Matches the InfoUI.js InfoDot popover (Sos2/InfoUI.js:120-126) so the two teaching hovers read
     as one language: white card, hairline border, 12px radius, soft shadow. Was a dark tooltip,
     which looked like a different product next to the axis popovers on the same page. */
  .lrs-info-pop{position:absolute;left:0;top:24px;z-index:20;width:260px;background:#fff;color:#334155;border:1px solid #e2e8f0;border-radius:12px;padding:11px 13px;font:.84rem/1.45 system-ui,sans-serif;box-shadow:0 8px 28px rgba(15,23,42,.16)}
  /* "Open guide →" inside the tooltip. Explicit resets on width/height/border-radius because
     public/css/style-analyze.css restyles loose button classes on the landing page. */
  .lrs-info-more{display:block;margin-top:8px;padding:0;border:0;background:none;color:#14536e;font:700 .78rem/1.3 system-ui,sans-serif;cursor:pointer;width:auto;height:auto;border-radius:0;text-align:left}
  .lrs-info-more:hover{text-decoration:underline}
  .lrs-metric-pick{display:flex;align-items:center;gap:8px}
  /* The label is the question; it reads at the control's size and in the site navy-teal, never
     smaller or greyer than the value it labels (CLAUDE.md UI rule 6). */
  .lrs-metric-pick label{font:700 13px/1 system-ui,sans-serif;color:#14536e}
  .lrs-metric-pick select{font:700 13px/1 system-ui,sans-serif;color:#14536e;border:1.5px solid #cbd5e1;border-radius:6px;padding:.3rem .5rem;cursor:pointer}
  .lrs-wrap2{display:flex;align-items:stretch;gap:12px}
  .lrs-gutter{display:flex;flex-direction:column;gap:6px;flex:0 0 auto}
  .lrs-rlab{display:flex;align-items:center;justify-content:flex-end;height:34px;font:800 11px/1 system-ui,sans-serif;letter-spacing:.04em;text-transform:uppercase;color:#5a6b76;white-space:nowrap;padding-right:2px}
  .lrs-rows{position:relative;flex:1 1 auto;min-width:0;display:flex;flex-direction:column;gap:6px}
  .lrs-row-empty{height:0}
  .lrs-bounds{position:absolute;inset:0;pointer-events:none;z-index:3}
  .lrs-bound{position:absolute;bottom:0;width:1.5px;background:rgba(17,43,55,.55)}

  /* ── NARROW SCREENS: keep the complete four-level shape in view ───────────────
     This landing-page widget has to communicate hierarchy before it has to make every
     individual scene a large tap target. A fixed 860px canvas hid most of the screenplay
     behind an undisclosed horizontal scroll on phones, so the rows now compress to the
     available width. Acts and sequences remain useful targets; the scene row reads as the
     screenplay-wide pattern and can still open a scene when a cell is wide enough. */
  @media (max-width: 900px) {
    .lrs-rows{overflow:visible;padding-bottom:0}
    .lrs-row,.lrs-row-empty{min-width:0}
    .lrs-cell{min-width:0}
    .lrs-row .lrs-cell{border-left-width:1px!important;border-right-width:0!important}
    .lrs-bounds{right:0;min-width:0}
  }
  .lrs-bound-act{width:2.5px;background:rgba(17,43,55,.9)}
  /* Phone minimap: the strip is a picture, not a control (see LRSnapshot.jsx, PHONE LAYOUT). */
  .lrs-wrap2--static .lrs-cell{cursor:default;pointer-events:none}
  .lrs-wrap2--static .lrs-cell:hover{filter:none;outline:none}
  /* Phone map (MapView.jsx): one row per unit, top to bottom, expanding in flow. */
  .lrs-map{margin-top:12px;border-top:1px solid #e3eaee;padding-top:6px;font-family:system-ui,sans-serif;color:#0f2430}
  .lrs-map-row{display:flex;align-items:stretch;gap:6px;border-bottom:1px solid #edf2f4}
  .lrs-map-main{display:grid;grid-template-columns:8px minmax(0,1fr) 16px;gap:10px;align-items:center;flex:1 1 auto;min-width:0;min-height:48px;padding:9px 2px 9px 0;border:0;background:none;text-align:left;cursor:pointer;font:inherit;color:inherit}
  .lrs-map-swatch{align-self:stretch;width:8px;border-radius:3px;background:var(--fill,#ccc)}
  .lrs-map-text{display:flex;flex-direction:column;gap:2px;min-width:0}
  .lrs-map-title{display:flex;flex-wrap:wrap;align-items:center;gap:4px 8px;font-size:15px;font-weight:800;line-height:1.25}
  .lrs-map-row--sequence .lrs-map-title{font-size:14px}
  .lrs-map-row--scene .lrs-map-title{font-size:13.5px;font-weight:700}
  .lrs-map-head{font-size:12.5px;line-height:1.4;color:#41565f}
  .lrs-map-row--scene .lrs-map-head{font-size:12px}
  .lrs-map-band{display:inline-block;border:1px solid transparent;border-radius:999px;padding:1px 7px;font-size:11px;font-weight:700;line-height:1.4}
  .lrs-map-chev{color:#5b7481;font-size:14px;font-weight:900;text-align:center}
  .lrs-map-open{flex:0 0 auto;align-self:center;min-height:32px;margin:0;padding:0 10px;border:1.5px solid #cbd5e1;border-radius:999px;background:#fff;color:#14536e;font:700 12px/1 system-ui,sans-serif;cursor:pointer}
  .lrs-map-open:hover,.lrs-map-open:focus-visible{border-color:#14536e}
  .lrs-map-children{margin-left:7px;padding-left:10px;border-left:2px solid #dfe7ec}
  .lrs-map-children .lrs-map-row:last-child{border-bottom:0}
  .lrs-hover{position:fixed;z-index:10000;background:#0f2430;color:#eef4f6;border-radius:9px;padding:10px 13px;box-shadow:0 14px 38px rgba(0,0,0,.36);pointer-events:none}
  .lrs-hover-top{display:flex;align-items:baseline;gap:8px;margin-bottom:4px}
  .lrs-hover-t{font:800 13px/1.25 system-ui,sans-serif}
  .lrs-hover-score{font:800 12px/1 system-ui,sans-serif;color:#0f2430;background:#eaf1f4;border-radius:5px;padding:2px 6px}
  .lrs-hover-head{font:700 12.5px/1.4 system-ui,sans-serif;color:#dbe7ec;margin-bottom:5px}
  .lrs-hover-sum{font:12px/1.5 system-ui,sans-serif;opacity:.8}
  `;
  const s = document.createElement('style');
  s.id = 'lrs-styles';
  s.textContent = css;
  document.head.appendChild(s);
}

function mount(el) {
  if (!el || el.__lrsMounted) return null;
  el.__lrsMounted = true;
  _injectStyles();

  const hash = el.dataset.hash || el.getAttribute('data-hash') || '';
  let config = {};
  try { config = JSON.parse(el.dataset.config || '{}'); } catch (e) { /* ignore malformed */ }

  const api = {}; // LRSnapshot fills api.setConfig on mount
  const root = createRoot(el);

  // Keep the root so setHash() can re-render the SAME element with a new hash instead of
  // forcing callers to mount one widget per script. LRSnapshot refetches on a hash change.
  const rec = { el, api, root, hash, config };

  root.render(<LRSnapshot hash={hash} initialConfig={config} apiRef={api} />);

  const key = el.id || `lrs-${registry.size + 1}`;
  registry.set(key, rec);
  return api;
}

// Programmatic control for callers that change display on the fly (no refetch).
//
// ⚠️ The patch is ALSO merged into `rec.config` (added 2026-08-21). Without that, a patch survives
// only until the next setHash(), which re-renders from `rec.config` — the ORIGINAL mount config —
// and silently reverts it. That bit the landing picker, where each script opens on its own reading
// (Lucy contract, The Fabelmans pressure): setConfig-then-setHash painted the previous script's
// colouring, and the failure is invisible because the chart renders perfectly, just wrong.
function setConfig(key, patch) {
  const rec = registry.get(key);
  if (!rec) return;
  rec.config = { ...rec.config, ...patch };
  if (typeof rec.api.setConfig === 'function') rec.api.setConfig(patch);
}

// Point an already-mounted widget at a different script. This DOES refetch
// (/lrsnapshot/data?hash=…). Used by the landing-page script picker.
// Returns true if the widget was found and re-rendered.
function setHash(key, hash) {
  const rec = registry.get(key);
  if (!rec || !hash || hash === rec.hash) return false;
  rec.hash = hash;
  rec.el.dataset.hash = hash;
  rec.root.render(<LRSnapshot hash={hash} initialConfig={rec.config} apiRef={rec.api} />);
  return true;
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-lrsnapshot]').forEach(mount);
});

// mount() is exported for elements added after DOMContentLoaded — there is no MutationObserver.
window.LRSnapshot = { mount, setConfig, setHash, registry };
